import { useState, forwardRef } from "react";
import {
 Typography,
 List,
 ListItem,
 Chip,
 ListItemText,
 IconButton,
 Button,
 Box,
 Menu,
 MenuItem,
 Divider,
 CircularProgress,
 Link,
 Dialog,
 AppBar,
 Toolbar,
 Slide,
 Container,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useNotifications, useDialog } from "hooks";
import { BreadcrumbsNavbar, NotificationTypography } from "components";

const Transition = forwardRef(function Transition(props, ref) {
 return <Slide direction="up" ref={ref} {...props} />;
});

export function NotificationsDialog({ open, onClose }) {
 const navigate = useNavigate();
 const { confirm } = useDialog();
 const {
  notifications,
  unreadCount,
  markAsRead,
  markAllAsRead,
  clearNotification,
  clearAllNotifications,
 } = useNotifications();

 const [anchorEl, setAnchorEl] = useState(null);
 const [selectedNotif, setSelectedNotif] = useState(null);
 const [loading, setLoading] = useState(false);

 const handleMenuOpen = (event, notif) => {
  event.stopPropagation();
  setAnchorEl(event.currentTarget);
  setSelectedNotif(notif);
 };

 const handleMenuClose = () => {
  setAnchorEl(null);
  setSelectedNotif(null);
 };

 const handleMarkAsRead = async () => {
  if (selectedNotif && !selectedNotif.read) {
   await markAsRead(selectedNotif.id);
  }
  handleMenuClose();
 };

 const handleClearNotification = async () => {
  if (selectedNotif) {
   await clearNotification(selectedNotif.id);
   handleMenuClose();
  }
 };

 const handleMarkAllAsRead = async () => {
  if (unreadCount === 0) return;
  setLoading(true);
  try {
   await markAllAsRead();
  } catch (error) {
   console.error("Error marking all as read:", error);
  } finally {
   setLoading(false);
  }
 };

 const handleClearAll = async () => {
  if (notifications.length === 0) return;

  const result = await confirm({
   title: "Clear all notifications",
   description: "Clear all notifications? This cannot be undone.",
   confirmLabel: "Clear all",
   cancelLabel: "Cancel",
  });

  if (result) {
   setLoading(true);
   try {
    await clearAllNotifications();
   } catch (error) {
    console.error("Error clearing all:", error);
   } finally {
    setLoading(false);
   }
  }
 };

 const handleNotificationClick = async (notif) => {
  if (!notif.read) {
   await markAsRead(notif.id);
  }

  if (notif.contextType === "restaurant") {
   onClose();
   navigate(`/restaurant/${notif.restaurantId}`);
  } else if (notif.contextType === "food") {
   onClose();
   navigate(`/food/edit/${notif.contextId}`);
  }
 };

 const formatNotificationStatus = (type) => {
  if (type.includes("approved")) return "Approved";
  if (type.includes("rejected")) return "Rejected";
  return "";
 };

 const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";
  const date = timestamp.toDate();
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
  if (diffMins < 10080) return `${Math.floor(diffMins / 1440)}d ago`;
  return date.toLocaleDateString();
 };

 return (
  <Dialog
   fullScreen
   open={open}
   onClose={onClose}
   TransitionComponent={Transition}
   sx={{
    "& .MuiDialog-paper": {
     marginTop: { xs: "56px", sm: "64px" },
     height: { xs: "calc(100% - 56px)", sm: "calc(100% - 64px)" },
     bgcolor: "background.default",
    },
   }}
  >
   {/* AppBar header */}
   <AppBar position="sticky" elevation={0}>
    <Container maxWidth="lg">
     <Toolbar disableGutters>
      <Box
       sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flex: 1,
        minWidth: 0,

        "& .MuiBreadcrumbs-root": {
         position: "static",
         bgcolor: "transparent",
         border: "none",
         p: 0,
         width: "auto",
         "& ol": {
          p: 0,
          maxWidth: "100%",
         },
        },
       }}
      >
       {" "}
       <BreadcrumbsNavbar
        onBack={onClose}
        title={
         <>
          Notifications
          {unreadCount > 0 && (
           <Chip
            label={`${unreadCount} unread`}
            size="small"
            color="primary"
            sx={{ ml: 1, height: 18, fontSize: "0.65rem" }}
           />
          )}
         </>
        }
       />
      </Box>

      {/* Action buttons */}
      <Box sx={{ display: "flex", gap: 1, mr: 1 }}>
       {unreadCount > 0 && (
        <Button
         startIcon={
          loading ? (
           <CircularProgress size={16} color="inherit" />
          ) : (
           <DoneAllIcon />
          )
         }
         onClick={handleMarkAllAsRead}
         disabled={loading}
         color="inherit"
         variant="outlined"
         size="small"
        >
         Mark all read
        </Button>
       )}
       {notifications.length > 0 && (
        <Button
         startIcon={
          loading ? (
           <CircularProgress size={16} color="inherit" />
          ) : (
           <ClearAllIcon />
          )
         }
         onClick={handleClearAll}
         disabled={loading}
         color="error"
         variant="outlined"
         size="small"
        >
         Clear all
        </Button>
       )}
      </Box>
     </Toolbar>
    </Container>
   </AppBar>

   {/* Content */}
   <Container
    maxWidth="lg"
    sx={{
     py: 3,
     px: { xs: 2, sm: 3 },
    }}
   >
    {notifications.length === 0 ? (
     <NotificationTypography
      Icon={NotificationsOffIcon}
      title="No Notifications"
      message="You're all caught up!"
      showActions={false}
     />
    ) : (
     <List disablePadding sx={{ p: 0 }}>
      {notifications.map((notif) => (
       <ListItem
        key={notif.id}
        disablePadding
        disableGutters
        onClick={() => handleNotificationClick(notif)}
        sx={{
         mb: 1,
         borderRadius: 1,
         transition: "all 0.2s",
         color: "inherit",
         "&:hover": {
          cursor: "pointer",
          bgcolor: "action.hover",
          transform: "translateX(4px)",
         },
        }}
        secondaryAction={
         <IconButton edge="end" onClick={(e) => handleMenuOpen(e, notif)}>
          <MoreVertIcon />
         </IconButton>
        }
       >
        {/* Unread dot on the left */}
        <Box
         sx={{
          width: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          alignSelf: "flex-start",
          pt: 1.5,
         }}
        >
         {!notif.read && (
          <Box
           sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            bgcolor: "primary.main",
           }}
          />
         )}
        </Box>
        <ListItemText
         disableTypography
         sx={{ my: 0.5, mr: 1 }}
         primary={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
           <Typography
            variant="subtitle1"
            sx={{ fontWeight: notif.read ? "normal" : "bold", flex: 1 }}
           >
            {notif.title}
           </Typography>
          </Box>
         }
         secondary={
          <Box>
           <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: notif.read ? "normal" : 500, mb: 0.5 }}
           >
            {notif.message}
           </Typography>
           <Typography variant="caption" color="text.secondary">
            {(() => {
             const statusText = formatNotificationStatus(notif.type);
             if (!statusText) return null;
             const statusColor =
              statusText === "Approved" ? "success.main" : "error.main";
             return (
              <>
               <Typography
                component="span"
                variant="caption"
                sx={{ color: statusColor, fontWeight: 700 }}
               >
                {statusText}
               </Typography>
               {" • "}
              </>
             );
            })()}

            {notif.contextTitle && (
             <>
              <Link
               component={RouterLink}
               to={`/restaurant/${notif.restaurantId}/deal/${notif.dealId}`}
               underline="hover"
               color="primary"
               sx={{ fontWeight: "bold", cursor: "pointer" }}
               onClick={(e) => e.stopPropagation()}
              >
               {notif.contextTitle}
              </Link>
              {" • "}
             </>
            )}

            {formatTimestamp(notif.createdAt)}
            {notif.triggeredBy && (
             <>
              {" • by "}
              {notif.triggeredBy.name}
             </>
            )}
           </Typography>
          </Box>
         }
        />
       </ListItem>
      ))}
     </List>
    )}
   </Container>

   {/* Context Menu */}
   <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
    {selectedNotif && !selectedNotif.read && (
     <MenuItem onClick={handleMarkAsRead}>
      <DoneAllIcon sx={{ mr: 1 }} /> Mark as read
     </MenuItem>
    )}
    {selectedNotif && !selectedNotif.read && <Divider />}
    <MenuItem onClick={handleClearNotification}>
     <DeleteIcon sx={{ mr: 1 }} /> Clear notification
    </MenuItem>
   </Menu>
  </Dialog>
 );
}
